import React from 'react';
import { MessageCircle, Thermometer, Cog, Fuel, Users } from 'lucide-react';
import { Button } from './ui/button';
import { Card, CardContent } from './ui/card';
import { useLanguage } from '../context/LanguageContext';

const FleetSection = () => {
  const { t, language } = useLanguage();
  const whatsappNumber = '21620505585';

  const vehicles = [
    {
      id: 1,
      name: 'VW Polo',
      category: 'Compact',
      image: 'https://images.unsplash.com/photo-1567696154185-4e2ec460e55c?crop=entropy&cs=srgb&fm=jpg&q=85&w=600',
      seats: 5,
      features: ['ac', 'manual', 'economic'],
    },
    {
      id: 2,
      name: 'Hyundai i10',
      category: 'City Car',
      image: 'https://images.pexels.com/photos/14776717/pexels-photo-14776717.jpeg?auto=compress&cs=tinysrgb&w=600',
      seats: 4,
      features: ['ac', 'manual', 'economic'],
    },
    {
      id: 3,
      name: 'Citadine Économique',
      category: 'Economy',
      image: 'https://images.pexels.com/photos/28837410/pexels-photo-28837410.jpeg?auto=compress&cs=tinysrgb&w=600',
      seats: 5,
      features: ['ac', 'manual', 'economic'],
    },
  ];

  const getFeatureIcon = (feature) => {
    switch (feature) {
      case 'ac':
        return <Thermometer className="w-4 h-4" />;
      case 'manual':
        return <Cog className="w-4 h-4" />;
      case 'economic':
        return <Fuel className="w-4 h-4" />;
      default:
        return null;
    }
  };

  const getWhatsAppLink = (vehicleName) => {
    const messages = {
      fr: `Bonjour! Je souhaite réserver le véhicule: ${vehicleName}`,
      en: `Hello! I would like to book the vehicle: ${vehicleName}`,
      de: `Hallo! Ich möchte das Fahrzeug buchen: ${vehicleName}`,
    };
    return `https://wa.me/${whatsappNumber}?text=${encodeURIComponent(messages[language])}`;
  };

  return (
    <section
      id="fleet"
      data-testid="fleet-section"
      className="py-16 sm:py-20 md:py-28 bg-accent/30"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-10 sm:mb-16">
          <span className="inline-block px-3 sm:px-4 py-1 sm:py-1.5 bg-[#00bfa5]/10 text-[#00bfa5] rounded-full text-xs sm:text-sm font-semibold mb-3 sm:mb-4">
            {t('fleet.title')}
          </span>
          <h2
            data-testid="fleet-title"
            className="text-2xl sm:text-3xl md:text-4xl font-bold text-[#606060] mb-3 sm:mb-4 font-['Outfit'] tracking-tight"
          >
            {t('fleet.title')}
          </h2>
          <p
            data-testid="fleet-subtitle"
            className="text-base sm:text-lg text-muted-foreground max-w-2xl mx-auto px-4"
          >
            {t('fleet.subtitle')}
          </p>
        </div>

        {/* Fleet Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6 lg:gap-8">
          {vehicles.map((vehicle, index) => (
            <Card
              key={vehicle.id}
              data-testid={`fleet-card-${vehicle.id}`}
              className="bg-white rounded-xl sm:rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-300 border-0 group"
            >
              {/* Image */}
              <div className="relative h-44 sm:h-52 overflow-hidden">
                <img
                  src={vehicle.image}
                  alt={vehicle.name}
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                />
                <div className="absolute top-3 sm:top-4 left-3 sm:left-4">
                  <span className="px-2.5 sm:px-3 py-1 bg-white/90 backdrop-blur-sm rounded-full text-xs font-semibold text-primary">
                    {vehicle.category}
                  </span>
                </div>
              </div>

              <CardContent className="p-4 sm:p-6">
                {/* Vehicle Name */}
                <h3 className="text-lg sm:text-xl font-bold text-[#606060] mb-2 font-['Outfit']">
                  {vehicle.name}
                </h3>

                {/* Features */}
                <div className="flex flex-wrap gap-1.5 sm:gap-2 mb-3 sm:mb-4">
                  <div className="flex items-center gap-1 sm:gap-1.5 text-xs sm:text-sm text-muted-foreground bg-[#00bfa5]/10 px-2 sm:px-3 py-1 sm:py-1.5 rounded-full">
                    <Users className="w-3 h-3 sm:w-4 sm:h-4 text-[#00bfa5]" />
                    <span>{vehicle.seats} {t('fleet.features.seats')}</span>
                  </div>
                  {vehicle.features.map((feature) => (
                    <div
                      key={feature}
                      className="flex items-center gap-1 sm:gap-1.5 text-xs sm:text-sm text-muted-foreground bg-[#00bfa5]/10 px-2 sm:px-3 py-1 sm:py-1.5 rounded-full"
                    >
                      <span className="text-[#00bfa5]">{getFeatureIcon(feature)}</span>
                      <span>{t(`fleet.features.${feature}`)}</span>
                    </div>
                  ))}
                </div>

                {/* Price */}
                <div className="flex items-center justify-between mb-3 sm:mb-4 pb-3 sm:pb-4 border-b border-border">
                  <span className="text-xs sm:text-sm text-[#00bfa5] font-medium">
                    {t('fleet.price')}
                  </span>
                </div>

                {/* CTA Button */}
                <a
                  href={getWhatsAppLink(vehicle.name)}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <Button
                    data-testid={`fleet-cta-${vehicle.id}`}
                    className="w-full bg-[#00bfa5] hover:bg-[#00a88f] text-white rounded-full h-12 sm:h-14 text-sm sm:text-base font-semibold transition-all flex items-center justify-center gap-2"
                  >
                    <MessageCircle className="w-4 h-4" />
                    {t('fleet.cta')}
                  </Button>
                </a>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    </section>
  );
};

export default FleetSection;
